import React, { useState, useEffect, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

/* ---- inject flip-card CSS once into <head> ---- */
const FLIP_STYLE_ID = 'admin-flashcard-flip-css';
const flipCSS = `
.afc-scene { perspective: 900px; height: 230px; cursor: pointer; }
.afc-card  { position: relative; width: 100%; height: 100%;
             transition: transform 0.6s ease; transform-style: preserve-3d; }
.afc-card.afc-flipped { transform: rotateY(180deg); }
.afc-face  { position: absolute; inset: 0; border-radius: 1rem;
             -webkit-backface-visibility: hidden; backface-visibility: hidden;
             display: flex; flex-direction: column; justify-content: space-between;
             padding: 1.25rem; overflow: hidden; }
.afc-front { background: linear-gradient(135deg,#ecfdf5 0%,#fff 100%);
             border: 1px solid #d1fae5; }
.afc-back  { background: linear-gradient(135deg,#1e293b 0%,#0f172a 100%);
             border: 1px solid #334155; transform: rotateY(180deg); }
`;
function ensureFlipStyles() {
    if (typeof document === 'undefined') return;
    if (!document.getElementById(FLIP_STYLE_ID)) {
        const s = document.createElement('style');
        s.id = FLIP_STYLE_ID;
        s.textContent = flipCSS;
        document.head.appendChild(s);
    }
}

function FlipCard({ r, onEdit, onDelete }: { r: any; onEdit: (r: any) => void; onDelete: (id: string, title: string) => void }) {
    const [flipped, setFlipped] = useState(false);
    const once = useRef(false);
    if (!once.current) { ensureFlipStyles(); once.current = true; }

    return (
        <div className="afc-scene" onClick={() => setFlipped(f => !f)}>
            <div className={`afc-card${flipped ? ' afc-flipped' : ''}`}>
                {/* ▸ Front — Question */}
                <div className="afc-face afc-front">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">
                                <i className="ri-question-line mr-1"></i>Question
                            </span>
                            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                <button onClick={() => onEdit(r)}
                                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer" title="Edit">
                                    <i className="ri-edit-line text-sm"></i>
                                </button>
                                <button onClick={() => onDelete(r._id, r.title)}
                                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete">
                                    <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                            </div>
                        </div>
                        <p className="text-slate-800 font-semibold text-sm leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.frontText}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-1.5 flex-wrap">
                            {r.subject && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{r.subject}</span>}
                            {r.level && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r.level}</span>}
                        </div>
                        <span className="text-[10px] text-emerald-400 flex items-center gap-1 font-medium">
                            <i className="ri-refresh-line"></i> click to flip
                        </span>
                    </div>
                </div>

                {/* ▸ Back — Answer */}
                <div className="afc-face afc-back">
                    <div>
                        <span className="text-[10px] font-bold text-sky-400 uppercase tracking-widest">
                            <i className="ri-lightbulb-line mr-1"></i>Answer
                        </span>
                        <p className="text-white font-semibold text-sm leading-relaxed mt-2" style={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.backText}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-slate-500 truncate max-w-[70%]">{r.title}</span>
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            <i className="ri-refresh-line"></i> click to flip back
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AdminFlashCards() {
    const [resources, setResources] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [subject, setSubject] = useState('');
    const [level, setLevel] = useState('');
    const [description, setDescription] = useState('');
    const [frontText, setFrontText] = useState('');
    const [backText, setBackText] = useState('');
    const [toasts, setToasts] = useState<any[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmPayload, setConfirmPayload] = useState<{ id: string; message: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterLevel, setFilterLevel] = useState('');

    const availableLevels = ['GCSE', 'A/L', 'Other'];

    const addToast = (message: string, type = 'success') => {
        const id = Date.now() + Math.random();
        setToasts(t => [...t, { id, message, type }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
    };

    const fetchResources = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/flashcards`);
            if (res.ok) setResources(await res.json());
        } catch (e) {
            console.error('Failed to fetch flash cards:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchResources();
    }, []);

    const resetForm = () => {
        setTitle(''); setSubject(''); setLevel('');
        setDescription(''); setFrontText(''); setBackText('');
        setEditingId(null); setShowAdd(false); setIsUploading(false);
    };

    const startEdit = (r: any) => {
        setEditingId(r._id);
        setTitle(r.title); setSubject(r.subject || '');
        setLevel(r.level || ''); setDescription(r.description || '');
        setFrontText(r.frontText || ''); setBackText(r.backText || '');
        setShowAdd(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !frontText || !backText) {
            addToast('Title, Question and Answer are required', 'error');
            return;
        }
        setIsUploading(true);
        try {
            const token = localStorage.getItem('token');
            const url = editingId ? `${API_BASE}/api/flashcards/${editingId}` : `${API_BASE}/api/flashcards`;
            const res = await fetch(url, {
                method: editingId ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ title, subject, level, description, frontText, backText, uploadedBy: 'Admin' })
            });
            if (res.ok) {
                const data = await res.json();
                setResources(editingId
                    ? resources.map(r => r._id === editingId ? data : r)
                    : [data, ...resources]
                );
                addToast(editingId ? 'Flash card updated!' : 'Flash card created!');
                resetForm();
            } else {
                const err = await res.json();
                addToast(err.error || 'Operation failed', 'error');
            }
        } catch {
            addToast('Error saving flash card', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteClick = (id: string, title: string) => {
        setConfirmPayload({ id, message: `Delete "${title}"? This cannot be undone.` });
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!confirmPayload) return;
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/flashcards/${confirmPayload.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setResources(prev => prev.filter(r => r._id !== confirmPayload.id));
                addToast('Flash card deleted');
            }
        } catch {
            addToast('Failed to delete flash card', 'error');
        } finally {
            setIsDeleting(false); setConfirmOpen(false); setConfirmPayload(null);
        }
    };

    const filteredResources = resources.filter(r => {
        const q = searchQuery.toLowerCase();
        const matchesSearch = (r.title || '').toLowerCase().includes(q)
            || (r.subject || '').toLowerCase().includes(q)
            || (r.frontText || '').toLowerCase().includes(q);
        const matchesLevel = !filterLevel || (r.level || '').toLowerCase() === filterLevel.toLowerCase();
        return matchesSearch && matchesLevel;
    });

    if (loading) return <div className="text-center py-12 text-slate-400">Loading flash cards...</div>;

    return (
        <div className="flex flex-col gap-5">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Flash Cards</h2>
                    <p className="text-slate-500 text-sm">{resources.length} card{resources.length !== 1 ? 's' : ''} — click any card to reveal the answer</p>
                </div>
                <button
                    onClick={() => { editingId ? resetForm() : setShowAdd(s => !s); }}
                    className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                    <i className={(showAdd || editingId) ? 'ri-close-line' : 'ri-add-line'}></i>
                    {(showAdd || editingId) ? 'Cancel' : 'Create Flash Card'}
                </button>
            </div>

            {/* Toasts */}
            <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
                {toasts.map(t => (
                    <div key={t.id} className={`min-w-[200px] px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg ${t.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
                        <i className={t.type === 'error' ? 'ri-error-warning-line' : 'ri-check-line'} /> {t.message}
                    </div>
                ))}
            </div>

            {/* Confirm Delete Modal */}
            {confirmOpen && confirmPayload && (
                <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                        <div className="w-12 h-12 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-4 text-2xl mx-auto">
                            <i className="ri-delete-bin-line"></i>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2 text-center">Delete Flash Card?</h3>
                        <p className="text-sm text-slate-500 mb-6 leading-relaxed text-center">{confirmPayload.message}</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-semibold hover:bg-slate-200 transition-colors cursor-pointer">Cancel</button>
                            <button onClick={confirmDelete} disabled={isDeleting} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-75">
                                {isDeleting ? <><i className="ri-loader-4-line animate-spin"></i>Deleting...</> : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create / Edit Form */}
            {(showAdd || editingId) && (
                <div className="bg-white rounded-2xl border border-emerald-200 p-6 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-5 flex items-center gap-2">
                        <i className="ri-stack-line text-emerald-600"></i>
                        {editingId ? 'Edit Flash Card' : 'Create New Flash Card'}
                    </h3>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Card Title *</label>
                            <input required value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Data Structures - Binary Trees" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Subject</label>
                            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Computer Science" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Level</label>
                            <select value={level} onChange={e => setLevel(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                                <option value="">Select Level</option>
                                {availableLevels.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optional)</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Key concepts for GCSE Binary Tree questions" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 h-14 resize-none" />
                        </div>

                        {/* Front */}
                        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
                            <label className="block text-sm font-bold text-emerald-700 mb-2">
                                <i className="ri-question-line mr-1"></i> Front — Question *
                            </label>
                            <textarea
                                required
                                value={frontText}
                                onChange={e => setFrontText(e.target.value)}
                                placeholder="e.g. What is the time complexity of binary search?"
                                className="w-full border border-emerald-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-emerald-500 min-h-[110px] resize-y bg-white text-slate-800"
                            />
                        </div>

                        {/* Back */}
                        <div className="bg-sky-50 rounded-xl border border-sky-100 p-4">
                            <label className="block text-sm font-bold text-sky-700 mb-2">
                                <i className="ri-lightbulb-line mr-1"></i> Back — Answer *
                            </label>
                            <textarea
                                required
                                value={backText}
                                onChange={e => setBackText(e.target.value)}
                                placeholder="e.g. O(log n) — it halves the search space each step"
                                className="w-full border border-sky-200 rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-sky-400 min-h-[110px] resize-y bg-white text-slate-800"
                            />
                        </div>

                        <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-slate-100">
                            <button type="button" onClick={resetForm} disabled={isUploading} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 cursor-pointer disabled:opacity-50">Cancel</button>
                            <button type="submit" disabled={isUploading} className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-6 py-2 rounded-lg transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-75 shadow-md shadow-emerald-700/20">
                                {isUploading ? <><i className="ri-loader-4-line animate-spin"></i>{editingId ? 'Updating...' : 'Saving...'}</> : (editingId ? 'Update Card' : 'Save Card')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                <div className="relative flex-1 min-w-[200px]">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input
                        type="text"
                        placeholder="Search by title, subject or question..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/20" style={{backgroundColor:'#f1f1f1'}}
                    />
                </div>
                <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} className="bg-slate-50 border-none rounded-lg px-3 py-2 text-sm text-slate-600 outline-none min-w-[130px]">
                    <option value="">All Levels</option>
                    {availableLevels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                {(searchQuery || filterLevel) && (
                    <button onClick={() => { setSearchQuery(''); setFilterLevel(''); }} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 px-3 py-2 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer">
                        Clear
                    </button>
                )}
            </div>

            {/* Cards Grid */}
            {filteredResources.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-stack-line text-2xl text-slate-300"></i>
                    </div>
                    <p className="font-semibold text-slate-600">{resources.length === 0 ? 'No flash cards yet' : 'No cards match your filters'}</p>
                    <p className="text-sm text-slate-400 mt-1">{resources.length === 0 ? 'Click "Create Flash Card" to add your first one.' : 'Try adjusting your search.'}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredResources.map(r => (
                        <FlipCard key={r._id} r={r} onEdit={startEdit} onDelete={handleDeleteClick} />
                    ))}
                </div>
            )}
        </div>
    );
}
